import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { UserRoleName } from '@sternum/web/features/user/models';

export interface UserToInvite {
  id: string;
  email: string;
  userRole: UserRoleName;
}

export interface UserTableRowState {
  changeRoleModeEnabled: boolean;
}

type RowId = string;

export interface UserState {
  usersToInvite: UserToInvite[];
  inviteUserInputValue: string;
  inviteUserRole: UserRoleName;
  hasInviteUserInputError: boolean;

  userTableRowsState: Record<RowId, UserTableRowState>;
}

const initialState: UserState = {
  usersToInvite: [],
  inviteUserInputValue: '',
  inviteUserRole: 'EDITOR',
  hasInviteUserInputError: false,
  userTableRowsState: {},
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    addUsersToInvite(state, action: PayloadAction<Omit<UserToInvite, 'id'>[]>) {
      state.usersToInvite.push(
        ...action.payload.map((userToInvite): UserToInvite => ({ ...userToInvite, id: createInviteUserId() })),
      );
    },
    removeUserToInvite(state, action: PayloadAction<UserToInvite>) {
      state.usersToInvite = state.usersToInvite.filter((userToInvite) => userToInvite.id !== action.payload.id);
    },
    changeUserRole(state, action: PayloadAction<{ userToInvite: UserToInvite; userRoleName: UserRoleName }>) {
      const userToInvite = state.usersToInvite.find((user) => user.id === action.payload.userToInvite.id);

      if (userToInvite) {
        userToInvite.userRole = action.payload.userRoleName;
      }
    },
    setInviteUserInputValue(state, action: PayloadAction<string>) {
      state.inviteUserInputValue = action.payload;
    },
    setInviteUserRole(state, action: PayloadAction<UserRoleName>) {
      state.inviteUserRole = action.payload;
    },
    setHasInviteUserInputError(state, action: PayloadAction<boolean>) {
      state.hasInviteUserInputError = action.payload;
    },
    clearUsersToInviteData(state) {
      state.usersToInvite = initialState.usersToInvite;
      state.inviteUserInputValue = initialState.inviteUserInputValue;
      state.inviteUserRole = initialState.inviteUserRole;
      state.hasInviteUserInputError = initialState.hasInviteUserInputError;
    },
    clearUserState() {
      return initialState;
    },

    setUserTableRowState(state, action: PayloadAction<{ rowId: RowId; state: Partial<UserTableRowState> }>) {
      state.userTableRowsState[action.payload.rowId] = {
        ...state.userTableRowsState[action.payload.rowId],
        ...action.payload.state,
      };
    },
    clearUserTableRowState(state, action: PayloadAction<{ rowId: RowId }>) {
      delete state.userTableRowsState[action.payload.rowId];
    },
  },
});

export const {
  addUsersToInvite,
  removeUserToInvite,
  changeUserRole,
  setInviteUserInputValue,
  setInviteUserRole,
  setHasInviteUserInputError,
  clearUsersToInviteData,
  clearUserState,
  setUserTableRowState,
  clearUserTableRowState,
} = userSlice.actions;

let prevPartialId = 1;

function createInviteUserId() {
  return `userId-${prevPartialId++}`;
}
